import { faSlashForward } from '@fortawesome/pro-light-svg-icons';
import { faComment } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { VoxelAlertProps } from '../../../components/voxel/Alert/VoxelAlert';
import VoxelAppBar from '../../../components/voxel/AppBar/VoxelAppBar';
import VoxelAppBarItem from '../../../components/voxel/AppBar/VoxelAppBarItem';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import Logo from '../../../components/voxel/Logo/Logo';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import { useIsLocalDev } from '../../../logic/otherUtilities/useIsLocalDev';
import { useSleek } from '../../../logic/sleekContext/useSleek';
import { useGetAlertList } from './Alerts/useGetAlertList';
import StartPageTeamButton from './StartPageTeamButton';
import StartPageUserButton from './StartPageUserButton';

export interface StartPageAppBarProps {
  user: CurrentUserGq;
  team: CurrentTeamGq;
  availableUsers: PublicUserGq[];
  window?: () => Window;
  children?: React.ReactElement;
  isScrolling?: boolean;
  loading?: boolean;
}

const StartPageAppBar: React.FC<StartPageAppBarProps> = ({
  user,
  isScrolling,
  loading,
  team,
  availableUsers,
  ...otherProps
}) => {
  // Route
  const history = useHistory();
  const match = useRouteMatch();

  // Toggle feedback
  const sleek = useSleek();

  useEffect(() => {
    sleek.setUser(user);
  }, []);

  const localDev = useIsLocalDev();

  const blockedAspects: string[] = [];
  if (user?.team?.blockMonthlyUseRankKw) blockedAspects.push('Rankings');
  if (user?.team?.blockMonthlyProxies) blockedAspects.push('Proxy usage');
  if (user?.team?.blockMonthlyUseSearchVolumeKw) blockedAspects.push('Search volume');
  if (user?.team?.blockMonthlyUseChatGptDrafts) blockedAspects.push('ChatGPT');
  if (user?.team?.blockMonthlyUseTranslateKw) blockedAspects.push('Keyword translations');

  const alertList: ReactElement<VoxelAlertProps>[] = useGetAlertList(user);

  return (
    <VoxelAppBar
      dense
      bgClass={'bg-white'}
      shadow={isScrolling}
      border={!isScrolling}
      loading={loading}
      isScrolling={isScrolling}
      voxelAlert={match.url === '/' ? alertList : undefined}
    >
      <div className="h-full w-full flex flex-row flex-nowrap items-center justify-between">
        <div className="h-full flex flex-row flex-nowrap items-center text-base font-medium font-sans">
          {/* Logo */}
          <a
            className="py-2 mr-5 hover:opacity-75 transform scale-50 -mx-10"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              history.push(`/`);
            }}
          >
            <Logo />
          </a>
          {/* Page buttons */}
          {[
            { url: '/', title: 'Projects', show: true },
            { url: '/quick-search', title: 'Quick search', show: true },
            { url: '/storybook', title: 'Storybook', show: localDev },
          ].map((item) => {
            let urlMatch = false;
            if (item.url.length === 1) urlMatch = match.url === item.url;
            else urlMatch = match.url.startsWith(item.url);
            return (
              item.show && (
                <VoxelAppBarItem
                  type="large"
                  key={item.title}
                  onClick={(event) => {
                    event.preventDefault();
                    history.push(item.url);
                  }}
                  href={item.url}
                  selected={urlMatch}
                >
                  {item.title}
                </VoxelAppBarItem>
              )
            );
          })}
        </div>
        <div className="h-full flex flex-row flex-nowrap items-center gap-6">
          {/* Feedback */}
          <VoxelButton
            size="md"
            title={'Feedback'}
            color={sleek.sleekOpen ? 'primary' : 'gray'}
            endIcon={<FontAwesomeIcon icon={faComment} />}
            onClick={() => sleek.toggleSleek()}
          />
          <div className="h-full flex flex-row flex-nowrap items-center gap-1">
            <StartPageTeamButton
              team={team}
              availableUsers={availableUsers}
              currentUser={user}
              appUsageBlocked={!!blockedAspects.length}
            />
            <FontAwesomeIcon icon={faSlashForward} size={'xl'} className="text-voxel-gray-300" />
            <StartPageUserButton user={user} />
          </div>
        </div>
      </div>
    </VoxelAppBar>
  );
};
export default StartPageAppBar;
